/**
 * Which live product groups have no Salesforce thumbnail — reported once per
 * catalogue load, so the answer comes from production rather than from reading
 * Salesforce.
 *
 * A group with no thumbnail falls to the designed placeholder tile, which is a
 * decided state and not a hole — so this is deliberately NOT an error. It tells
 * ops which WorkType still needs a `Thumbnail__c`.
 *
 * ONE event per catalogue load, carrying every uncovered code. Not one per
 * group: the catalogue lists them all at once, so per-group would put a dozen
 * breadcrumbs in Sentry for a single page view. `warn` rather than `info` on
 * purpose: `info` reaches nowhere in production, and production is the only
 * place the answer lives.
 */

import { logger } from 'utils/logging'

/** Group codes that are icon-only by design — their cards carry a glyph, and
 *  only the opened panel ever had a picture. Absent artwork is correct here, so
 *  they are not reported. */
const ICON_ONLY_GROUP_CODES: ReadonlySet<string> = new Set([
  'CONSTRUCTION_FILE',
  'DOCUMENTATION',
  'LAND_REGISTRATION',
  'LAW_INQUIRIES',
  'MUNICIPAL',
  'PROTECTION',
  'AUTHORITIES_DOCUMENT',
  'ENERGY_CERTIFICATE',
])

/** Groups that should never reach a catalogue card at all — a fee is not a
 *  product someone browses. Reporting them as "missing artwork" would send
 *  someone to draw a picture of an invoice. */
const NON_CATALOGUE_GROUP_CODES: ReadonlySet<string> = new Set([
  'INVOICE_FEE',
  'PREPAYMENT',
])

interface ArtworkSubject {
  productGroupCode?: string
  preview?: unknown
}

/**
 * The distinct group codes rendering without artwork, sorted, excluding the two
 * sets above. Pure — the reporting is the caller's, so this is testable without
 * a logger.
 */
export const uncoveredArtworkGroups = (shells: ReadonlyArray<ArtworkSubject>): string[] => {
  const uncovered = new Set<string>()
  for (const shell of shells) {
    const code = shell.productGroupCode
    if (!code || shell.preview) continue
    if (ICON_ONLY_GROUP_CODES.has(code) || NON_CATALOGUE_GROUP_CODES.has(code)) continue
    uncovered.add(code)
  }
  return [...uncovered].sort()
}

/** Report the catalogue's uncovered groups, once. Silent when there are none. */
export const reportArtworkCoverage = (shells: ReadonlyArray<ArtworkSubject>, countryCode: string): void => {
  const uncovered = uncoveredArtworkGroups(shells)
  if (uncovered.length === 0) return

  logger.warn(
    'Product groups rendering without a Salesforce thumbnail',
    { productGroupCodes: uncovered, countryCode },
    {
      context: {
        component: 'purchaseFlowRevampCatalogue.transform',
        action: 'reportArtworkCoverage',
        metadata: {
          productGroupCodes: uncovered.join(','),
          uncoveredCount: String(uncovered.length),
          countryCode,
        },
      },
    }
  )
}
